import { Chocobo } from '@/models/chocobos'
import { ChocoboFactory } from '@/services/chocoboFactory'
import { EffectManager } from '@/services/effectManager'

export class BreedingManager {
  static validateBreedingPair(mother, father) {
    if (!mother || !father) {
      return { valid: false, reason: 'Both parents must be present' }
    }

    if (mother.gender !== 'female' || father.gender !== 'male') {
      return { valid: false, reason: 'Must be bred from a male and female' }
    }

    if (mother.level < 5 || father.level < 5) {
      return { valid: false, reason: 'Both parents must be at least level 5' }
    }

    return { valid: true }
  }

  // Alias for backward compatibility
  static validateBreeding(mother, father) {
    return this.validateBreedingPair(mother, father)
  }

  static inheritBaseStats(mother, father) {
    const motherInheritChance = 0.7
    
    // Helper function to get speed stat from either baseSpeed or speed property
    const getSpeedStat = (parent) => {
      return parent.baseSpeed !== undefined ? parent.baseSpeed : parent.speed
    }
    
    // Helper function to get stamina stat from either baseStamina or stamina property
    const getStaminaStat = (parent) => {
      return parent.baseStamina !== undefined ? parent.baseStamina : parent.stamina
    }
    
    // Speed inheritance
    let baseSpeed
    if (Math.random() < motherInheritChance) {
      // Inherit mother's speed with small variation
      const motherSpeed = getSpeedStat(mother)
      baseSpeed = Math.max(1, Math.floor(motherSpeed * (0.9 + Math.random() * 0.2)))
    } else {
      // Inherit father's speed with small variation
      const fatherSpeed = getSpeedStat(father)
      baseSpeed = Math.max(1, Math.floor(fatherSpeed * (0.9 + Math.random() * 0.2)))
    }

    // Stamina inheritance
    let baseStamina
    if (Math.random() < motherInheritChance) {
      // Inherit mother's stamina with small variation
      const motherStamina = getStaminaStat(mother)
      baseStamina = Math.max(1, Math.floor(motherStamina * (0.9 + Math.random() * 0.2)))
    } else {
      // Inherit father's stamina with small variation
      const fatherStamina = getStaminaStat(father)
      baseStamina = Math.max(1, Math.floor(fatherStamina * (0.9 + Math.random() * 0.2)))
    }

    // Generate initial stat bonuses
    const speedBonus = Math.floor(Math.random() * 40) + 1
    const staminaBonus = Math.floor(Math.random() * 40) + 1

    return {
      baseSpeed,
      baseStamina,
      speedBonus,
      staminaBonus
    }
  }

  static inheritBehavior(mother, father) {
    const higherLevelParent = mother.level >= father.level ? mother : father
    const lowerLevelParent = mother.level >= father.level ? father : mother
    
    const inheritFromHigherChance = 0.7
    const randomValue = Math.random()
    
    // Inherit from higher level parent (70% chance)
    if (randomValue < inheritFromHigherChance) {
      if (higherLevelParent.behaviorId >= 3 && higherLevelParent.behaviorId <= 8){
        return higherLevelParent.behaviorId - 2
      } else {
        return higherLevelParent.behaviorId
      }
    }
    
    // Inherit from lower level parent (30% chance)
    if (lowerLevelParent.behaviorId > 3){
      return lowerLevelParent.behaviorId + 2
    } else {
      return lowerLevelParent.behaviorId
    }
  }

  static inheritXFactors(mother, father) {
    // Chance to inherit each X-Factor is 50% if parent has it
    return {
      hasSpeedXFactor: (mother.hasSpeedXFactor && Math.random() < 0.5) || 
                       (father.hasSpeedXFactor && Math.random() < 0.5),
      hasStaminaXFactor: (mother.hasStaminaXFactor && Math.random() < 0.5) || 
                         (father.hasStaminaXFactor && Math.random() < 0.5)
    }
  }

  static determineOffspringColor(mother, father) {
    // Black and Yellow combination
    if ((mother.color === 'black' && father.color === 'yellow') ||
        (mother.color === 'yellow' && father.color === 'black')) {
      return Math.random() < 0.01 ? 'gold' : 'yellow'
    }

    // Special color combinations
    const specialColors = ['green', 'pink', 'blue']
    if (specialColors.includes(mother.color) && specialColors.includes(father.color)) {
      return 'black'
    }

    // High-level yellow breeding
    if (mother.color === 'yellow' && father.color === 'yellow' && 
        mother.level >= 20 && father.level >= 20) {
      const random = Math.random() * 100
      if (random < 15) return 'blue'
      if (random < 30) return 'green'
      if (random < 45) return 'pink'
      if (random < 80) return 'yellow'
      return 'yellow'
    }

    // Default to mother's color for other combinations
    return mother.color
  }

  static determineOffspringRegion() {
    // Bred chocobos are always marked as stable-born
    return 'region7'
  }

  static breedChocobos(mother, father, currentDay = 1) {
    // Validate breeding pair
    const validation = this.validateBreedingPair(mother, father)
    if (!validation.valid) {
      throw new Error(validation.reason)
    }

    // Get breeding enhancement effects from consumed items
    const motherEffects = EffectManager.getBreedingEffects(mother)
    const fatherEffects = EffectManager.getBreedingEffects(father)
    const combinedEffects = this.combineBreedingEffects(motherEffects, fatherEffects)

    // Generate offspring properties with enhanced color logic
    const color = this.determineOffspringColorWithEffects(mother, father, combinedEffects)
    const region = this.determineOffspringRegion()
    const name = ChocoboFactory.generateRandomName()

    // Create offspring
    const offspring = new Chocobo(color, region, name)
    offspring.birthDay = currentDay

    // Inherit base stats and bonuses with enhancement effects
    const inheritedStats = this.inheritBaseStatsWithEffects(mother, father, combinedEffects)
    
    // Override the base stats and bonuses
    offspring.baseSpeed = inheritedStats.baseSpeed
    offspring.baseStamina = inheritedStats.baseStamina
    offspring.speedBonus = inheritedStats.speedBonus
    offspring.staminaBonus = inheritedStats.staminaBonus
    offspring.initialSpeedBonus = inheritedStats.speedBonus
    offspring.initialStaminaBonus = inheritedStats.staminaBonus

    // Inherit behavior with enhancement effects
    offspring.behaviorId = this.inheritBehaviorWithEffects(mother, father, combinedEffects)

    // Inherit X-Factors
    const xFactors = this.inheritXFactors(mother, father)
    offspring.hasSpeedXFactor = xFactors.hasSpeedXFactor
    offspring.hasStaminaXFactor = xFactors.hasStaminaXFactor

    // If offspring has X-Factors, apply the multiplier to initial bonuses
    if (offspring.hasSpeedXFactor) {
      offspring.speedBonus *= 2
      offspring.initialSpeedBonus = offspring.speedBonus
    }
    if (offspring.hasStaminaXFactor) {
      offspring.staminaBonus *= 2
      offspring.initialStaminaBonus = offspring.staminaBonus
    }

    // Update total stats
    offspring.updateTotalStats()

    return offspring
  }

  static combineBreedingEffects(motherEffects, fatherEffects) {
    // Combine effects from both parents, taking the better values
    return {
      perfect_inheritance: motherEffects.perfect_inheritance || fatherEffects.perfect_inheritance,
      perfect_speed_inheritance: motherEffects.perfect_speed_inheritance || fatherEffects.perfect_speed_inheritance,
      perfect_stamina_inheritance: motherEffects.perfect_stamina_inheritance || fatherEffects.perfect_stamina_inheritance,
      perfect_behavior_inheritance: motherEffects.perfect_behavior_inheritance || fatherEffects.perfect_behavior_inheritance,
      stat_bonus: Math.max(motherEffects.stat_bonus, fatherEffects.stat_bonus),
      speed_bonus: Math.max(motherEffects.speed_bonus, fatherEffects.speed_bonus),
      stamina_bonus: Math.max(motherEffects.stamina_bonus, fatherEffects.stamina_bonus),
      behavior_bonus: Math.max(motherEffects.behavior_bonus, fatherEffects.behavior_bonus),
      rare_color_boost: Math.max(motherEffects.rare_color_boost, fatherEffects.rare_color_boost),
      gold_color_boost: Math.max(motherEffects.gold_color_boost, fatherEffects.gold_color_boost),
      black_to_gold_boost: Math.max(motherEffects.black_to_gold_boost, fatherEffects.black_to_gold_boost)
    }
  }

  static inheritBaseStatsWithEffects(mother, father, effects) {
    // If perfect inheritance is enabled, take the best stats from both parents
    if (effects.perfect_inheritance || effects.perfect_speed_inheritance || effects.perfect_stamina_inheritance) {
      const getSpeedStat = (parent) => parent.baseSpeed !== undefined ? parent.baseSpeed : parent.speed
      const getStaminaStat = (parent) => parent.baseStamina !== undefined ? parent.baseStamina : parent.stamina
      
      let baseSpeed, baseStamina
      
      // Handle speed inheritance
      if (effects.perfect_inheritance || effects.perfect_speed_inheritance) {
        baseSpeed = Math.max(getSpeedStat(mother), getSpeedStat(father))
        if (effects.speed_bonus > 0) {
          baseSpeed = Math.floor(baseSpeed * (1 + effects.speed_bonus / 100))
        }
      } else {
        // Use original inheritance logic
        const motherInheritChance = 0.7
        if (Math.random() < motherInheritChance) {
          const motherSpeed = getSpeedStat(mother)
          baseSpeed = Math.max(1, Math.floor(motherSpeed * (0.9 + Math.random() * 0.2)))
        } else {
          const fatherSpeed = getSpeedStat(father)
          baseSpeed = Math.max(1, Math.floor(fatherSpeed * (0.9 + Math.random() * 0.2)))
        }
      }
      
      // Handle stamina inheritance
      if (effects.perfect_inheritance || effects.perfect_stamina_inheritance) {
        baseStamina = Math.max(getStaminaStat(mother), getStaminaStat(father))
        if (effects.stamina_bonus > 0) {
          baseStamina = Math.floor(baseStamina * (1 + effects.stamina_bonus / 100))
        }
      } else {
        // Use original inheritance logic
        const motherInheritChance = 0.7
        if (Math.random() < motherInheritChance) {
          const motherStamina = getStaminaStat(mother)
          baseStamina = Math.max(1, Math.floor(motherStamina * (0.9 + Math.random() * 0.2)))
        } else {
          const fatherStamina = getStaminaStat(father)
          baseStamina = Math.max(1, Math.floor(fatherStamina * (0.9 + Math.random() * 0.2)))
        }
      }
      
      // Apply general stat bonus if any
      if (effects.stat_bonus > 0) {
        baseSpeed = Math.floor(baseSpeed * (1 + effects.stat_bonus / 100))
        baseStamina = Math.floor(baseStamina * (1 + effects.stat_bonus / 100))
      }
      
      // Generate initial stat bonuses
      const speedBonus = Math.floor(Math.random() * 40) + 1
      const staminaBonus = Math.floor(Math.random() * 40) + 1
      
      return {
        baseSpeed,
        baseStamina,
        speedBonus,
        staminaBonus
      }
    } else {
      // Use original inheritance logic
      return this.inheritBaseStats(mother, father)
    }
  }

  static determineOffspringColorWithEffects(mother, father, effects) {
    // Black and Yellow combination with boost
    if ((mother.color === 'black' && father.color === 'yellow') ||
        (mother.color === 'yellow' && father.color === 'black')) {
      const goldChance = 0.01 + effects.gold_color_boost + effects.black_to_gold_boost
      return Math.random() < goldChance ? 'gold' : 'yellow'
    }

    // Special color combinations
    const specialColors = ['green', 'pink', 'blue']
    if (specialColors.includes(mother.color) && specialColors.includes(father.color)) {
      // With rare color boost, there's a chance to get a different special color
      if (effects.rare_color_boost > 0 && Math.random() < effects.rare_color_boost) {
        const otherSpecialColors = specialColors.filter(c => c !== mother.color && c !== father.color)
        return otherSpecialColors[Math.floor(Math.random() * otherSpecialColors.length)]
      }
      return 'black'
    }

    // High-level yellow breeding with enhanced chances
    if (mother.color === 'yellow' && father.color === 'yellow' && 
        mother.level >= 20 && father.level >= 20) {
      const random = Math.random() * 100
      const rareBoostPercent = effects.rare_color_boost * 100
      
      if (random < 15 + rareBoostPercent) return 'blue'
      if (random < 30 + rareBoostPercent) return 'green'
      if (random < 45 + rareBoostPercent) return 'pink'
      if (random < 80) return 'yellow'
      return 'yellow'
    }

    // General rare color boost for other combinations
    if (effects.rare_color_boost > 0 && Math.random() < effects.rare_color_boost) {
      const rareColors = ['blue', 'green', 'pink']
      return rareColors[Math.floor(Math.random() * rareColors.length)]
    }

    // Default to mother's color for other combinations
    return mother.color
  }

  static inheritBehaviorWithEffects(mother, father, effects) {
    if (effects.perfect_inheritance || effects.perfect_behavior_inheritance) {
      // Take the better behavior ID (lower is better)
      let bestBehavior = Math.min(mother.behaviorId, father.behaviorId)
      
      // Apply behavior bonus (reduce behavior ID, but keep it at least 1)
      if (effects.behavior_bonus > 0) {
        bestBehavior = Math.max(1, bestBehavior - effects.behavior_bonus)
      }
      
      return bestBehavior
    } else {
      // Use original inheritance logic
      return this.inheritBehavior(mother, father)
    }
  }
}